import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RootState } from '../../store/store';
import {
  changeDeleteTestResultModalVisibility,
  changeShowAlertTestResultModalVisibility,
  changeUpdateBiomarkersResultsModalVisibility,
  setAlertsList,
  setAlertsListByAppointment,
  setSelectedTestResultId,
  setSelectedTestResultMemberId,
  setStaffAlerts,
  setStaffAlertsError,
  setAlertsCount
} from '../../store/alertsSlice';

import TableHeader from '../../components/BaseComponents/TableHeader';
import TableRowActionMenu from '../../components/BaseComponents/TableRowActionMenu';
import ConfirmModal from '../BaseComponents/Modal/ConfirmModal';
import TestResultsModal from '../Members/TestResults/TestResultsModal';

import { deleteBiomarkerResult, getBiomarkersResultsByAppointment } from '../../api/biomarkers';
import { getNoteById } from '../../api/members';
import { readStaffNotification, Alert } from '../../api/staff';
import { getUnprocessedTestResultsCount } from '../../api/testResults';
import { getNotesCount } from '../../api/counters';
import { setNotesCount } from '../../store/appSlice';
import { BiomarkerResultType } from '../../types/apiGeneratedTypes';
import { filterAlertsForDisplay, sortAlertsByPriority } from '../../utils/alertFilters';

import AlertRow from './AlertRow';
import ViewNoteModal from './ViewNoteModal';

const HEADER_COLUMNS = [
  { title: 'Date', key: 'created_date', minWidth: '120px', flex: 1 },
  { title: 'Member Name', key: 'member_name', minWidth: '300px' },
  { title: 'Appointment date', key: 'appointment_date', minWidth: '180px', flex: 1 },
  { title: 'Alert type', key: 'alert_type', minWidth: '150px', flex: 1 },
  { title: 'Status', key: 'status', minWidth: '200px', flex: 1 },
  { title: '', key: 'actions', minWidth: '80px', flex: 1 }
];

const ALERT_TYPE_MAP: Record<string, string> = {
  'note_mention': 'Note',
  'biomarker_awaiting': 'Biomarker result',
  'biomarker_urgent': 'Biomarker result',
  'bio_age_calculated': 'Biological age',
  'biomarker_pending': 'Biomarker results',
  'follow_on_created': 'Follow-on',
  'report_review_overdue': 'Biomarker result',
  'report_discussion_overdue': 'Biomarker result',
  'medical_card_incomplete': 'Member',
  'tuli_appointment_complete_overdue': 'Tuli appointment',
  'nutritionist_invite_overdue': 'Appointment',
  'future_test_today': 'Future test'
};

const STATUS_CONFIG: Record<string, { class: string; text: string; icon?: string }> = {
  'note_mention': { class: 'mentioned-in-note', text: 'Mentioned in Note', icon: '/icons/at_mentioned.png' },
  'biomarker_awaiting': { class: 'awaiting-discussion', text: 'Awaiting Discussion', icon: '/icons/Dialog.png' },
  'biomarker_urgent': { class: 'urgent-result', text: 'Urgent Result' },
  'bio_age_calculated': { class: 'unpublished', text: 'Unpublished', icon: '/icons/Forbidden Circle.png' },
  'biomarker_pending': { class: 'biomarker-pending', text: 'Pending Approval', icon: '/icons/Hourglass.png' },
  'follow_on_created': { class: 'mentioned-in-note', text: 'Follow-on Created', icon: '/icons/Refresh.png' },
  'report_review_overdue': { class: 'overdue-alert', text: 'Report awaiting review by GP' },
  'report_discussion_overdue': { class: 'overdue-alert', text: 'Report Awaiting Discussion' },
  'medical_card_incomplete': { class: 'member-active', text: 'Member Active' },
  'tuli_appointment_complete_overdue': { class: 'tuli-appt-complete', text: 'Tuli Appt Complete' },
  'nutritionist_invite_overdue': { class: 'nutritionist-appt-invite-sent', text: 'Nutritionist Appt invite sent' },
  'future_test_today': { class: 'future-test-today', text: 'Future Test' }
};

const AlertsTable: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const {
    alertsList,
    selectedTestResultId,
    isDeleteTestResultModalVisible,
    alertsListByAppointment,
    isUpdateBiomarkersResultsModalVisible,
    selectedTestResultMemberId,
    staffAlerts,
    staffAlertsError
  } = useSelector((state: RootState) => state.alerts);
  
  const [isDeletingResults, setIsDeletingResults] = useState(false);
  const [isDeleteError, setIsDeleteError] = useState<any>(null);
  const [, setIsStaffAlertActionLoading] = useState(false);
  const [isViewNoteModalOpen, setIsViewNoteModalOpen] = useState(false);
  const [selectedNoteData, setSelectedNoteData] = useState<any>(null);
  const [isLoadingNote, setIsLoadingNote] = useState(false);
  const [, setIsLoadingFollowOn] = useState(false);

  const refreshAlertsCount = useCallback(async () => {
    try {
      const alerts = await getUnprocessedTestResultsCount();
      dispatch(setAlertsCount(alerts.count));
    } catch (error) {
      console.log('Error refreshing alerts count', error);
    }
  }, [dispatch]);

  const refreshNotesCount = useCallback(async () => {
    try {
      const notes = await getNotesCount();
      dispatch(setNotesCount(notes.total_count));
    } catch (error) {
      console.log('Error refreshing notes count', error);
    }
  }, [dispatch]);

  useEffect(() => {
    const doRefresh = async () => {
      try {
        await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
      } catch (e) {
      }
    };
    doRefresh();
    const intervalId = setInterval(doRefresh, 30000);
    return () => clearInterval(intervalId);
  }, [refreshAlertsCount, refreshNotesCount]);

  const getAlertTypeDisplay = useCallback((type: string): string => {
    return ALERT_TYPE_MAP[type] || type.toUpperCase();
  }, []);

  const getStatusInfo = useCallback((alertData: any) => {
    const config = STATUS_CONFIG[alertData.type];
    if (config) {
      return config;
    }
    return {
      class: alertData.is_read ? 'read' : 'unread',
      text: alertData.is_read ? 'Read' : 'Unread',
      icon: null
    };
  }, []);

  const handleStaffAlertClick = useCallback(async (alert: Alert) => {
    if (alert.type !== 'note_mention' && alert.type !== 'follow_on_created' &&
      alert.type !== 'medical_card_incomplete' && alert.type !== 'report_review_overdue' &&
      alert.type !== 'report_discussion_overdue' && alert.type !== 'tuli_appointment_complete_overdue' &&
      alert.type !== 'nutritionist_invite_overdue' && alert.type !== 'future_test_today') return;

    if (!alert.is_read) {
      try {
        await readStaffNotification(alert.id);
        if (staffAlerts) {
          const updatedAlerts = staffAlerts.map(a => 
            a.id === alert.id ? { ...a, is_read: true } : a
          );
          dispatch(setStaffAlerts(updatedAlerts));
        }
        // Refresh both counts when note mention is clicked
        await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
      } catch {
      }
    }
    
    const memberId = alert.metadata?.member_id || 
                    alert.metadata?.member_user_id || 
                    (alert as any).member_id || 
                    (alert as any).member_user_id ||
                    (alert as any).user_id;
    
    if (memberId) {
      const noteId = alert.metadata?.note_id || alert.note_id;
      if (noteId && alert.type === 'note_mention') {
        try {
          setIsLoadingNote(true);
          const noteData = await getNoteById(noteId);
          navigate(`/members/${memberId}`, { 
            state: { 
              openNoteModal: true, 
              noteData: noteData,
              alertId: alert.id
            } 
          });
        } catch {
          navigate(`/members/${memberId}`);
        } finally {
          setIsLoadingNote(false);
        }
      } else if (alert.type === 'follow_on_created') {
        const followOnAlert = alert as any; // Type assertion for follow-on specific properties
        const followOnId = followOnAlert.metadata?.follow_on_id || followOnAlert.follow_on_id;
        
        if (followOnId) {
          try {
            setIsLoadingFollowOn(true);
            // Fetch the actual follow-on data from the API
            const { getFollowOnById } = await import('../../api/follow-on');
            const followOnData = await getFollowOnById(followOnId);
            
            navigate(`/members/${memberId}`, { 
              state: { 
                activeTab: 'notes', 
                activeSubTab: 'followOnAlerts',
                openFollowOnModal: true,
                followOnData: followOnData
              } 
            });
          } catch (error) {
            console.error('Error fetching follow-on data:', error);
            // Fallback to basic navigation without modal
            navigate(`/members/${memberId}`, { 
              state: { 
                activeTab: 'notes', 
                activeSubTab: 'followOnAlerts'
              } 
            });
          } finally {
            setIsLoadingFollowOn(false);
          }
        } else {
          // No follow-on ID available, just navigate to the tab
          navigate(`/members/${memberId}`, { 
            state: { 
              activeTab: 'notes', 
              activeSubTab: 'followOnAlerts'
            } 
          });
        }
      } else {
        navigate(`/members/${memberId}`);
      }
    } else {
      if (alert.member_name) {
        toast.info('Member ID not found. Please check the alert data structure.');
      }
    }
  }, [navigate, staffAlerts, dispatch, refreshAlertsCount, refreshNotesCount]);

  const handleStatusBadgeClick = useCallback(async (alert: Alert, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (alert.type === 'note_mention' || alert.type === 'follow_on_created') {
      await handleStaffAlertClick(alert);
    } else if (alert.type === 'biomarker_pending' || alert.type === 'biomarker_awaiting' || alert.type === 'biomarker_urgent') {
      const appointmentId = alert.metadata?.appointment_id || alert.appointment_id;
      const memberId = alert.metadata?.member_id || alert.metadata?.member_user_id;
      
      if (!appointmentId || !memberId) {
        toast.error('Appointment ID or Member ID not found in alert data');
        return;
      }
      
      try {
        setIsStaffAlertActionLoading(true);
        dispatch(setSelectedTestResultId(appointmentId));
        dispatch(setSelectedTestResultMemberId(memberId));
        
        const response = await getBiomarkersResultsByAppointment(
          appointmentId,
          BiomarkerResultType.UNPROCESSED
        );
        dispatch(setAlertsListByAppointment(response.biomarkers));
        dispatch(changeShowAlertTestResultModalVisibility(true));
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch biomarker results');
      } finally {
        setIsStaffAlertActionLoading(false);
      }
    } else if (alert.type === 'bio_age_calculated') {
      const memberId = alert.metadata?.member_id || 
                      (alert.metadata as any)?.user_id || 
                      alert.metadata?.member_user_id;
      
      if (!alert.is_read) {
        try {
          await readStaffNotification(alert.id);
          if (staffAlerts) {
            const updatedAlerts = staffAlerts.map(a => 
              a.id === alert.id ? { ...a, is_read: true } : a
            );
            dispatch(setStaffAlerts(updatedAlerts));
          }
          await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
        } catch (error) {
          console.error('Failed to mark bio age notification as read:', error);
        }
      }
      
      if (memberId) {
        navigate(`/members/${memberId}`, {
          state: {
            activeTab: 'biologicalAge'
          }
        });
      } else {
        toast.error('Member ID not found in alert data');
      }
    }
  }, [dispatch, handleStaffAlertClick, navigate, refreshAlertsCount, refreshNotesCount, staffAlerts]);


  const handleUnifiedAlertAction = useCallback(async (id: number, action: string) => {
    try {
      setIsStaffAlertActionLoading(true);
      
      const staffAlert = staffAlerts?.find(alert => alert.id === id);
      
      if (staffAlert) {
        if (action === 'view') {
           if (staffAlert.type === 'biomarker_pending' || staffAlert.type === 'biomarker_awaiting' || staffAlert.type === 'biomarker_urgent') {
             const appointmentId = staffAlert.metadata?.appointment_id || staffAlert.appointment_id;
             const memberId = staffAlert.metadata?.member_id || staffAlert.metadata?.member_user_id;
             
             if (!appointmentId || !memberId) {
               toast.error('Appointment ID or Member ID not found in alert data');
               return;
             }
             
             dispatch(setSelectedTestResultId(appointmentId));
             dispatch(setSelectedTestResultMemberId(memberId));
             
             const response = await getBiomarkersResultsByAppointment(
               appointmentId,
               BiomarkerResultType.UNPROCESSED
             );
             dispatch(setAlertsListByAppointment(response.biomarkers));
             dispatch(changeShowAlertTestResultModalVisibility(true));
           } else if (staffAlert.type === 'note_mention') {
             await handleStaffAlertClick(staffAlert);
           } else if (staffAlert.type === 'bio_age_calculated') {
             const memberId = staffAlert.metadata?.member_id || 
                             (staffAlert.metadata as any)?.user_id || 
                             staffAlert.metadata?.member_user_id;
             
             if (!staffAlert.is_read) {
               try {
                 await readStaffNotification(staffAlert.id);
                 if (staffAlerts) {
                   const updatedAlerts = staffAlerts.map(a => 
                     a.id === staffAlert.id ? { ...a, is_read: true } : a
                   );
                   dispatch(setStaffAlerts(updatedAlerts));
                 }
                 await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
               } catch (error) {
                 console.error('Failed to mark bio age notification as read:', error);
               }
             }
             
             if (memberId) {
               navigate(`/members/${memberId}`, {
                 state: {
                   activeTab: 'biologicalAge'
                 }
               });
             } else {
               toast.error('Member ID not found in alert data');
             }
           } else {
             toast.success('Viewing staff alert details');
           }
        } else if (action === 'mark-read') {
          try {
            await readStaffNotification(id);
            if (staffAlerts) {
              const updatedAlerts = staffAlerts.map(alert => 
                alert.id === id ? { ...alert, is_read: true } : alert
              );
              dispatch(setStaffAlerts(updatedAlerts));
            }
            await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
            toast.success('Alert marked as read');
          } catch (error: any) {
            toast.error(error.message || 'Failed to mark alert as read');
          }
        } else if (action === 'delete') {
          toast.success('Alert deleted successfully');
        }
      } else {
        if (action === 'delete') {
          dispatch(setSelectedTestResultId(id));
          const response = await getBiomarkersResultsByAppointment(
            id,
            BiomarkerResultType.UNPROCESSED
          );
          dispatch(setAlertsListByAppointment(response.biomarkers));
          dispatch(changeDeleteTestResultModalVisibility(true));
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to perform action');
    } finally {
      setIsStaffAlertActionLoading(false);
    }
  }, [dispatch, handleStaffAlertClick, navigate, refreshAlertsCount, refreshNotesCount, staffAlerts]);

  const handleDeleteResults = useCallback(async () => {
    try {
      setIsDeletingResults(true);
      setIsDeleteError(null);
      
      const results_ids = alertsListByAppointment
        ?.map((alert) => alert.result.id)
        .filter((id) => id) as number[];
        
      if (!results_ids || results_ids.length === 0) {
        throw new Error('No results to delete');
      }
      
      if (staffAlerts) {
        const alertToMarkAsRead = staffAlerts.find(alert => {
          const appointmentId = alert.metadata?.appointment_id || alert.appointment_id;
          const isBiomarkerAlert = alert.type === 'biomarker_pending' || alert.type === 'biomarker_awaiting';
          return isBiomarkerAlert && appointmentId === selectedTestResultId && !alert.is_read;
        });
        
        if (alertToMarkAsRead) {
          try {
            await readStaffNotification(alertToMarkAsRead.id);
          } catch (error) {
            console.warn('Failed to mark alert as read, continuing with deletion:', error);
          }
        }
      }
      
      await deleteBiomarkerResult(results_ids);
      
      if (alertsList) {
        const updatedAlertsList = alertsList.filter(
          (alert) => alert.appointment_id !== selectedTestResultId
        );
        dispatch(setAlertsList(updatedAlertsList));
      }
      
      if (staffAlerts) {
        const updatedStaffAlerts = staffAlerts.filter(
          (alert) => {
            const appointmentId = alert.metadata?.appointment_id || alert.appointment_id;
            const isBiomarkerAlert = alert.type === 'biomarker_pending' || alert.type === 'biomarker_awaiting';
            const shouldRemove = isBiomarkerAlert && appointmentId === selectedTestResultId;
            
            if (shouldRemove) {
              console.log('Removing staff alert with ID:', alert.id, 'for appointment:', appointmentId);
            }
            
            return !shouldRemove;
          }
        );
        dispatch(setStaffAlerts(updatedStaffAlerts));
      }
      
      dispatch(changeDeleteTestResultModalVisibility(false));
      dispatch(changeShowAlertTestResultModalVisibility(false));
      dispatch(setSelectedTestResultId(null));
      
      // Refresh both counts after deletion
      await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
      
      toast.success('Biomarker results deleted successfully');
    } catch (error: any) {
      console.error('Error during deletion:', error);
      setIsDeleteError(error);
      toast.error(error.message || 'Failed to delete biomarker results');
    } finally {
      setIsDeletingResults(false);
    }
  }, [alertsList, alertsListByAppointment, selectedTestResultId, staffAlerts, dispatch, refreshAlertsCount, refreshNotesCount]);

  const unifiedAlerts = useMemo(() => {
    const allAlerts: Array<{alert: any, type: 'staff' | 'biomarker', key: string}> = [];
    
    if (staffAlerts?.length) {
      // Filter staff alerts based on Notion document requirements
      const filteredStaffAlerts = filterAlertsForDisplay(staffAlerts);
      
      filteredStaffAlerts.forEach((alert) => {
        if (alert && alert.id && (alert.metadata?.member_name || alert.title)) {
          allAlerts.push({
            alert,
            type: 'staff',
            key: `staff-${alert.id}`
          });
        }
      });
    }
    
    if (alertsList?.length) {
      alertsList.forEach((alert) => {
        if (alert && alert.appointment_id && alert.member_name) {
          allAlerts.push({
            alert,
            type: 'biomarker',
            key: `biomarker-${alert.appointment_id}`
          });
        }
      });
    }
    
    // Sort alerts by priority and date
    return sortAlertsByPriority(allAlerts.map(item => item.alert)).map(alert => {
      const existingItem = allAlerts.find(item => item.alert.id === alert.id);
      return existingItem || { alert, type: 'staff' as const, key: `staff-${alert.id}` };
    });
  }, [staffAlerts, alertsList]);

  const renderUnifiedAlertRow = useCallback((alertData: any, alertType: 'staff' | 'biomarker') => {
    if (alertType === 'staff') {
      const isNoteMention = alertData.type === 'note_mention' || alertData.type === 'follow_on_created';
      const isBiomarkerPending = alertData.type === 'biomarker_pending';
      const statusInfo = getStatusInfo(alertData);
      const isClickable = alertData.type === 'note_mention' || alertData.type === 'follow_on_created' ||
        alertData.type === 'biomarker_pending' || alertData.type === 'biomarker_awaiting' ||
        alertData.type === 'biomarker_urgent' || alertData.type === 'bio_age_calculated' ||
        alertData.type === 'medical_card_incomplete' || alertData.type === 'report_review_overdue' ||
        alertData.type === 'report_discussion_overdue' || alertData.type === 'tuli_appointment_complete_overdue' ||
        alertData.type === 'nutritionist_invite_overdue' || alertData.type === 'future_test_today';

      const handleRowClick = async () => {
        if (isNoteMention) {
          handleStaffAlertClick(alertData);
        } else if (isBiomarkerPending || alertData.type === 'biomarker_awaiting' || alertData.type === 'biomarker_urgent') {
          handleStatusBadgeClick(alertData, { stopPropagation: () => {} } as React.MouseEvent);
        } else if (alertData.type === 'bio_age_calculated') {
          const memberId = alertData.metadata?.member_id || 
                          (alertData.metadata as any)?.user_id || 
                          alertData.metadata?.member_user_id;
          
          if (!alertData.is_read) {
            try {
              await readStaffNotification(alertData.id);
              if (staffAlerts) {
                const updatedAlerts = staffAlerts.map(a => 
                  a.id === alertData.id ? { ...a, is_read: true } : a
                );
                dispatch(setStaffAlerts(updatedAlerts));
              }
              await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
            } catch (error) {
              console.error('Failed to mark bio age notification as read:', error);
            }
          }
          
          if (memberId) {
            navigate(`/members/${memberId}`, {
              state: {
                activeTab: 'biologicalAge'
              }
            });
          } else {
            toast.error('Member ID not found in alert data');
          }
        } else if (alertData.type === 'medical_card_incomplete' ||
          alertData.type === 'report_review_overdue' ||
          alertData.type === 'report_discussion_overdue' ||
          alertData.type === 'tuli_appointment_complete_overdue' ||
          alertData.type === 'nutritionist_invite_overdue' ||
          alertData.type === 'future_test_today') {
          // Handle new alert types - navigate to member page
          const memberId = alertData.metadata?.member_id || 
                          (alertData.metadata as any)?.user_id || 
                          alertData.metadata?.member_user_id;
          
          if (!alertData.is_read) {
            try {
              await readStaffNotification(alertData.id);
              if (staffAlerts) {
                const updatedAlerts = staffAlerts.map(a => 
                  a.id === alertData.id ? { ...a, is_read: true } : a
                );
                dispatch(setStaffAlerts(updatedAlerts));
              }
              await Promise.all([refreshAlertsCount(), refreshNotesCount()]);
            } catch (error) {
              console.error('Failed to mark alert as read:', error);
            }
          }
          
          if (memberId) {
            navigate(`/members/${memberId}`);
          } else {
            toast.error('Member ID not found in alert data');
          }
        }
      };
      
      const createdAtRaw = alertData.created_at || '';
      const appointmentDateRaw = alertData.metadata?.appointment_date || alertData.appointment_date || '';
      const createdAtDateOnly = createdAtRaw ? String(createdAtRaw).split('T')[0].split(' ')[0] : '';
      const appointmentDateOnly = appointmentDateRaw ? String(appointmentDateRaw).split('T')[0].split(' ')[0] : '';

      return (
        <div 
          className={`table-row alerts-table-row ${alertData.is_read ? 'read' : 'unread'} ${isClickable ? 'clickable-row' : ''}`}
          onClick={isClickable ? handleRowClick : undefined}
          key={`staff-${alertData.id}`}
          role={isClickable ? "button" : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onKeyDown={isClickable ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleRowClick();
            }
          } : undefined}
          aria-label={isClickable ? `${alertData.title || 'Alert'} - ${alertData.is_read ? 'Read' : 'Unread'} alert` : undefined}
        >
          <div className='created-date' title={createdAtDateOnly || 'N/A'}>
            {createdAtDateOnly}
          </div>
          <div className='alert-member-name' title={alertData.metadata?.member_name || 'Unknown Member'}>
            {alertData.metadata?.member_name || 'Unknown Member'}
          </div>
          <div className='appointment-date' title={appointmentDateOnly || 'N/A'}>
            {appointmentDateOnly}
          </div>
          <div className='biomarkers-count'>
            {getAlertTypeDisplay(alertData.type)}
          </div>
          <div className='status-container'>
            <span 
              className={`status-badge ${statusInfo.class} ${isClickable ? 'clickable-badge' : ''}`}
              aria-label={`Alert status: ${statusInfo.text}`}
              onClick={isClickable ? (e) => {
                e.stopPropagation();
                handleStatusBadgeClick(alertData, e);
              } : undefined}
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={isClickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStatusBadgeClick(alertData, e as any);
                }
              } : undefined}
            >
              {statusInfo.icon && statusInfo.icon !== 'null' && (
                <img 
                  src={statusInfo.icon} 
                  alt="" 
                  className='status-icon'
                  width="16"
                  height="16"
                />
              )}
              {statusInfo.text}
            </span>
          </div>
          <div className='actions'>
            <TableRowActionMenu
              options={[{ key: 'view', value: 'View Details' }]}
              id={alertData.id}
              onActionMenuClick={handleUnifiedAlertAction}
            />
          </div>
        </div>
      );
    }
    
    return <AlertRow alertData={alertData} key={`biomarker-${alertData.appointment_id}`} />;
  }, [
    dispatch,
    getAlertTypeDisplay,
    getStatusInfo,
    handleStaffAlertClick,
    handleStatusBadgeClick,
    handleUnifiedAlertAction,
    navigate,
    refreshAlertsCount,
    refreshNotesCount,
    staffAlerts,
  ]);

  return (
    <div className='scroll-container'>
      <div className='alerts-section'>
        <div className='table-container alerts-table'>
          <TableHeader headerColumns={HEADER_COLUMNS} gap='24px' />
          <div className='table-body alerts-table-body'>
            {unifiedAlerts.length > 0 ? (
              unifiedAlerts
                .filter(({ alert }) => {
                  if (!alert) {
                    console.log('Filtering out null/undefined alert');
                    return false;
                  }

                  if (
                    alert.type &&
                    (
                      alert.type === 'note_mention' ||
                      alert.type === 'biomarker_pending' ||
                      alert.type === 'biomarker_awaiting' ||
                      alert.type === 'bio_age_calculated' ||
                      alert.type === 'medical_card_incomplete' ||
                      alert.type === 'report_review_overdue' ||
                      alert.type === 'report_discussion_overdue' ||
                      alert.type === 'tuli_appointment_complete_overdue' ||
                      alert.type === 'nutritionist_invite_overdue' ||
                      alert.type === 'follow_on_created' ||
                      alert.type === 'future_test_today' ||
                      alert.type === 'biomarker_urgent'
                    )
                  ) {
                    return alert.id && (alert.metadata?.member_name || alert.title);
                  }
                  
                  if (alert.appointment_id) {
                    return alert.member_name;
                  }
                  
                  return true;
                })
                .map(({ alert, type }) => 
                  renderUnifiedAlertRow(alert, type)
                )
            ) : (
              <div className='no-alerts-message'>
                {/* <p>No alerts available</p> */}
              </div>
            )}
          </div>
        </div>
      </div>

      {staffAlertsError && (
        <div className='alerts-section'>
          <h3 className='section-title'>Staff Alerts Error</h3>
          <div className='error-container'>
            <h3>Error loading staff alerts</h3>
            <p>{staffAlertsError}</p>
            <button 
              onClick={() => dispatch(setStaffAlertsError(null))}
              className='retry-button'
            >
              Retry
            </button>
          </div>
        </div>
      )}

      
      {isDeleteTestResultModalVisible && (
        <ConfirmModal
          isOpen={isDeleteTestResultModalVisible}
          onClose={() => dispatch(changeDeleteTestResultModalVisibility(false))}
          onConfirmClick={handleDeleteResults}
          title='Delete biomarker results?'
          subtitle='This action will permanently remove the results. Once deleted, it cannot be restored.'
          loading={isDeletingResults}
          error={isDeleteError ? 'Error deleting results' : undefined}
        />
      )}
      
      {isUpdateBiomarkersResultsModalVisible && (
        <TestResultsModal
          isOpen={isUpdateBiomarkersResultsModalVisible}
          onClose={() => dispatch(changeUpdateBiomarkersResultsModalVisibility(false))}
          mode='update'
          selectedAppointmentIdForTestResults={selectedTestResultId!}
          userId={selectedTestResultMemberId!}
          testResultsData={alertsListByAppointment!}
        />
      )}
      
      <ViewNoteModal
        isOpen={isViewNoteModalOpen}
        onClose={() => {
          setIsViewNoteModalOpen(false);
          setSelectedNoteData(null);
        }}
        noteData={selectedNoteData}
        isLoading={isLoadingNote}
      />
    </div>
  );
};

export default AlertsTable;
